import { Avatar, Flex, Text } from "@fluentui/react-northstar";
import React from "react";
import {
  CSS_CLASS,
  LIFECYCLE,
  TicketApprovalState,
} from "../../shared/utils/constants";
import approvalColored from "../../../images/print/lifecycle/approval-colored.png";
import checkIcon from "../../../images/print/lifecycle/check.png";
import crossIcon from "../../../images/print/lifecycle/cross.png";
import calendarIcon from "../../../images/print/lifecycle/calendar.png";
import infoIcon from "../../../images/print/lifecycle/info.png";
import { useTranslation } from "react-i18next";
import { getShortDate } from "../ticketHelper";

type ApprovalPrintProps = {
  ticketApprovals: TicketApproval[];
  currentUserEmail: string;
};

export const ApprovalPrint: React.FC<ApprovalPrintProps> = ({
  ticketApprovals,
  currentUserEmail,
}) => {
  const { t } = useTranslation();
  if (ticketApprovals.length === 0) return null;
  return (
    <div className="border-t pt-4 mb-1 w-full mt-4">
      <Text weight="semibold" className="mb-2 print-text-color">
        {LIFECYCLE.APPORVALS}
      </Text>
      <div>
        {ticketApprovals.map(({ Approval: approval }, index) => (
          <div className={`w-full my-5 border-b pb-4`} key={approval.Id}>
            <div className="flex items-center mb-1 justify-between">
              <div className="flex items-center mb-1">
                <img src={approvalColored} alt="approval icon" width="16px" />
                <Text size="large" weight="semibold" className="ml-1 print-text-color">
                  {LIFECYCLE.APPROVAL} {index + 1}: {approval.Title}
                </Text>
              </div>
              <div>
                {approval.ApprovalState === TicketApprovalState.Approved && (
                  <Flex hAlign={"end"} className={"cui-pt-4 cui-pv-4"}>
                    <div className={`${CSS_CLASS.STATUS_PILL_ACTIVE}`}>
                      <Text weight="bold" size="smaller">
                        {t("ticket-details.edit-ticket-form.approval.approved")}
                      </Text>
                    </div>
                  </Flex>
                )}
                {approval.ApprovalState === TicketApprovalState.Rejected && (
                  <Flex hAlign={"end"} className={"cui-pt-4 cui-pv-4"}>
                    <div className={`${CSS_CLASS.STATUS_PILL_RESOLVED}`}>
                      <Text weight="bold" size="smaller">
                        {t("ticket-details.edit-ticket-form.approval.rejected")}
                      </Text>
                    </div>
                  </Flex>
                )}
                {approval.ApprovalState === TicketApprovalState.Pending && (
                  <Flex hAlign={"end"} className={"cui-pt-4 cui-pv-4"}>
                    <div className={`status-pill ${CSS_CLASS.STATUS_PILL_OTHERS}`}>
                      <Text weight="bold" size="smaller">
                        {t("ticket-details.edit-ticket-form.approval.pending")}
                      </Text>
                    </div>
                  </Flex>
                )}
              </div>
            </div>
            {approval.Approvers.map(({ Approver, HasApproved }) => (
              <div
                className="flex items-center justify-between mb-2 print-text-color"
                key={Approver.Id}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <div>
                      <Avatar name={Approver.FullName} className="avatar" size="smaller" />
                    </div>
                    <div>
                      <div>
                        {Approver.Email === currentUserEmail
                          ? "You"
                          : Approver.FullName}
                      </div>
                      {HasApproved === TicketApprovalState.Approved && (
                        <div>
                          {t(
                            "ticket-details.edit-ticket-form.approval.approved"
                          )}{" "}
                          {LIFECYCLE.APPROVAL}
                        </div>
                      )}
                      {HasApproved === TicketApprovalState.Rejected && (
                        <div>
                          {t(
                            "ticket-details.edit-ticket-form.approval.rejected"
                          )}{" "}
                          {LIFECYCLE.APPROVAL}
                        </div>
                      )}
                      {HasApproved === TicketApprovalState.Pending && (
                        <div>
                          {t(
                            "ticket-details.edit-ticket-form.approval.pending"
                          )}{" "}
                          {LIFECYCLE.APPROVAL}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {Approver.Email === currentUserEmail && HasApproved === TicketApprovalState.Pending && (
                  <div className="flex items-center gap-2">
                    <img src={checkIcon} alt="approved icon" width="16px" />
                    <img src={crossIcon} alt="rejected icon" width="16px" />
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center gap-2 border-t pt-2 print-text-color">
              <span>
                <img src={infoIcon} alt="info icon" width="16px" />
              </span>
              <div className="flex items-center gap-1">
                <span>
                  {t(
                    "ticket-details.edit-ticket-form.approval.additional-info"
                  )}
                </span>
                <span>{approval.AdditionalDetails}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 print-text-color">
              <span>
                <img src={calendarIcon} alt="cal icon" width="16px" />
              </span>
              <div className="flex items-center gap-1">
                <span>
                  {t("ticket-details.edit-ticket-form.approval.requested-on")}
                </span>
                <span>{getShortDate(approval.CreatedDate)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
